import { useState, useEffect, useRef } from "react";
import { PurchaseReview } from "../components/PurchaseReview";
import { CollectibleStoreSection } from "../components/collectibles/CollectibleStoreSection";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Music, 
  Calendar, 
  ShoppingBag, 
  MapPin, 
  Play,
  Pause,
  Users,
  Sparkles,
  Heart,
  Check,
  MessageCircle,
  Send,
  UserPlus,
  UserMinus,
  ThumbsUp,
  BadgeCheck,
  Volume2,
  SkipForward,
  Video,
  X,
  Share2,
  Twitter,
  Link as LinkIcon,
  Mail,
  Globe,
  Crown,
  Star,
  Phone,
  ShieldCheck,
  Download,
  User
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import TeaserModal from "../components/TeaserModal";
import TipButton from "../components/TipButton";

const CURRENCIES = [
  { code: "usd", symbol: "$", label: "USD" },
  { code: "cad", symbol: "C$", label: "CAD" },
];

const ArtistProfile = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [aiOfferings, setAiOfferings] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [collectibles, setCollectibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [subCurrency, setSubCurrency] = useState(CURRENCIES[0]);

  // Load user's currency preference
  useEffect(() => {
    if (token) {
      axios.get(`${API}/settings/currency`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const saved = CURRENCIES.find(c => c.code === res.data.preferred_currency);
          if (saved) setSubCurrency(saved);
        }).catch(() => {});
    }
  }, [token]);
  const [activeTab, setActiveTab] = useState("content");
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [playingId, setPlayingId] = useState(null);
  const [likes, setLikes] = useState({});
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const videoRefs = useRef({});
  const progressInterval = useRef(null);

  const pageviewTracked = useRef(null);

  // Teaser modal state — clicking Play Teaser on any priced product opens it.
  const [teaser, setTeaser] = useState({ open: false, item: null, purchaseState: null });
  // Purchase map: product_id -> { owned, downloads_used, downloads_remaining, max_downloads }
  const [ownedMap, setOwnedMap] = useState({});

  useEffect(() => {
    // Fetch my orders once to know which products the viewer has already bought.
    if (!token) return;
    axios.get(`${API}/orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const map = {};
        (r.data?.orders || []).forEach((o) => {
          if (o.product_id) {
            map[o.product_id] = {
              owned: true,
              downloads_used: o.downloads_used || 0,
              downloads_remaining: Number.isFinite(o.downloads_remaining) ? o.downloads_remaining : 3,
              max_downloads: o.max_downloads || 3,
            };
          }
        });
        setOwnedMap(map);
      })
      .catch(() => {});
  }, [token]);

  const openTeaser = (product) => {
    const own = ownedMap[product.product_id];
    setTeaser({
      open: true,
      item: {
        title: product.name,
        previewUrl: product.preview_url,
        posterUrl: (product.images && product.images[0]) || null,
        contentType: product.category === "video" ? "video" : (product.category === "music" ? "music" : "music"),
        productId: product.product_id,
        priceLabel: `$${Number(product.price).toFixed(2)}`,
        artistName: artist?.stage_name,
      },
      purchaseState: own ? {
        owned: true,
        downloadsUsed: own.downloads_used,
        downloadsRemaining: own.downloads_remaining,
        maxDownloads: own.max_downloads,
      } : { owned: false },
    });
  };

  useEffect(() => {
    fetchArtist();
    // Track page view for hourly traffic analytics (no auth required)
    // Guard against React StrictMode double-invocation
    if (artistId && pageviewTracked.current !== artistId) {
      pageviewTracked.current = artistId;
      axios.post(`${API}/analytics/pageview?artist_id=${artistId}`).catch(() => {});
    }
  }, [artistId]);

  const fetchArtist = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/artists/${artistId}`),
        axios.get(`${API}/events?artist_id=${artistId}`),
        axios.get(`${API}/products?artist_id=${artistId}`),
        axios.get(`${API}/content?artist_id=${artistId}`),
        axios.get(`${API}/subscriptions/tiers/${artistId}`),
        axios.get(`${API}/social/followers/${artistId}`),
        axios.get(`${API}/experiences/artist/${artistId}`),
        axios.get(`${API}/ai-messages/offerings/artist/${artistId}`),
        axios.get(`${API}/leaderboard/artist/${artistId}`),
        axios.get(`${API}/collectibles/artist/${artistId}`)
      ];
      
      const settled = await Promise.allSettled(requests);
      if (settled[0].status === "rejected") throw settled[0].reason;
      if (settled.slice(1).some((result) => result.status === "rejected")) {
        toast.error("Some artist sections could not load. Please retry shortly.");
      }
      const [artistRes, eventsRes, productsRes, contentRes, tiersRes, followersRes, experiencesRes, aiOfferingsRes, leaderboardRes, collectiblesRes] = settled.map((result) => result.status === "fulfilled" ? result.value : { data: [] });
      setArtist(artistRes.data);
      setEvents(eventsRes.data);
      setProducts(productsRes.data);
      setContent(contentRes.data);
      setSubscriptionTiers(tiersRes.data);
      setFollowerCount(followersRes.data.follower_count || 0);
      setExperiences(experiencesRes.data);
      setAiOfferings(aiOfferingsRes.data);
      setLeaderboard(leaderboardRes.data);
      setCollectibles(collectiblesRes.data);
      
      // Check follow status if logged in
      if (token) {
        try {
          const followRes = await axios.get(`${API}/social/is-following/${artistId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFollowing(followRes.data.following);
        } catch {}
      }
      
      // Fetch likes for each content
      const likeData = {};
      for (const item of contentRes.data) {
        try {
          const likeRes = await axios.get(`${API}/social/likes/${item.content_id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          likeData[item.content_id] = likeRes.data;
        } catch {}
      }
      setLikes(likeData);
    } catch (e) {
      console.error("Failed to fetch artist:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      const redirectUrl = window.location.origin + `/artist/${artistId}`;
      window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }
    try {
      const endpoint = following ? "unfollow" : "follow";
      const res = await axios.post(`${API}/social/${endpoint}`, { artist_id: artistId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowing(res.data.following);
      setFollowerCount(prev => following ? prev - 1 : prev + 1);
      toast.success(following ? "Unfollowed" : "Now following!");
    } catch (e) {
      toast.error("Failed to update follow status");
    }
  };

  const handleLike = async (contentId) => {
    if (!isAuthenticated) {
      const redirectUrl = window.location.origin + `/artist/${artistId}`;
      window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }
    try {
      const res = await axios.post(`${API}/social/like/${contentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikes(prev => ({
        ...prev,
        [contentId]: { ...prev[contentId], liked: res.data.liked, like_count: res.data.like_count, user_liked: res.data.liked }
      }));
    } catch (e) {
      toast.error("Failed to like");
    }
  };

  const loadComments = async (contentId) => {
    try {
      const res = await axios.get(`${API}/social/comments/${contentId}`);
      setComments(prev => ({ ...prev, [contentId]: res.data }));
    } catch {}
  };

  const handleComment = async (contentId) => {
    if (!commentText.trim()) return;
    if (!isAuthenticated) {
      toast.error("Please sign in to comment");
      return;
    }
    try {
      const res = await axios.post(`${API}/social/comments/${contentId}`, { text: commentText }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => ({
        ...prev,
        [contentId]: [res.data, ...(prev[contentId] || [])]
      }));
      setCommentText("");
      toast.success("Comment added!");
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  const togglePlay = (contentId) => {
    if (playingId === contentId) {
      audioRef.current?.pause();
      clearInterval(progressInterval.current);
      setPlayingId(null);
      setProgress(0);
      setCurrentTime(0);
    } else {
      if (audioRef.current) { audioRef.current.pause(); clearInterval(progressInterval.current); }
      Object.values(videoRefs.current).forEach(v => { if (v && !v.paused) v.pause(); });
      
      const audio = new Audio(`${API}/content/${contentId}/stream`);
      audioRef.current = audio;
      
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      audio.play().catch(() => toast.error("Playback failed"));
      
      progressInterval.current = setInterval(() => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
          setCurrentTime(audio.currentTime);
        }
      }, 200);
      
      audio.onended = () => {
        setPlayingId(null);
        setProgress(0);
        setCurrentTime(0);
        clearInterval(progressInterval.current);
      };
      setPlayingId(contentId);
    }
  };

  const toggleVideo = (contentId) => {
    const video = videoRefs.current[contentId];
    if (!video) return;
    if (playingId === contentId) {
      video.pause();
      clearInterval(progressInterval.current);
      setPlayingId(null);
    } else {
      if (audioRef.current) { audioRef.current.pause(); clearInterval(progressInterval.current); }
      Object.values(videoRefs.current).forEach(v => { if (v && !v.paused) v.pause(); });
      video.play().catch(() => toast.error("Playback failed"));
      progressInterval.current = setInterval(() => {
        if (video.duration) {
          setProgress((video.currentTime / video.duration) * 100);
          setCurrentTime(video.currentTime);
          setDuration(video.duration);
        }
      }, 200);
      video.onended = () => { setPlayingId(null); setProgress(0); clearInterval(progressInterval.current); };
      setPlayingId(contentId);
    }
  };

  const seekTo = (e, contentId, isVideo = false) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (isVideo && videoRefs.current[contentId]) {
      videoRefs.current[contentId].currentTime = pct * videoRefs.current[contentId].duration;
    } else if (audioRef.current && playingId === contentId) {
      audioRef.current.currentTime = pct * audioRef.current.duration;
    }
    setProgress(pct * 100);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const handleSubscribe = async (tierId) => {
    if (!isAuthenticated) {
      const redirectUrl = window.location.origin + `/artist/${artistId}`;
      window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    if (Number(subscriptionTiers.find((tier) => tier.tier_id === tierId)?.price) !== 0) {
      setReviewPurchase({ item_type: "subscription", item_id: tierId, quantity: 1 });
      return;
    }
    setSubscribing(tierId);
    try {
      const response = await axios.post(
        `${API}/subscriptions/subscribe/${tierId}?origin_url=${encodeURIComponent(window.location.origin)}&currency=${subCurrency.code}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.free) { toast.success("Membership activated"); fetchArtist(); }
    } catch (e) {
      console.error("Subscribe failed:", e);
      toast.error(e.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSubscribing(null);
    }
  };

  const [bookingExpId, setBookingExpId] = useState(null);
  const [reviewPurchase, setReviewPurchase] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(null);
  const [aiFanName, setAiFanName] = useState("");
  const [aiCustomMsg, setAiCustomMsg] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const handleBookExperience = async (experienceId) => {
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    setReviewPurchase({ item_type: "experience", item_id: experienceId, quantity: 1 });
  };

  const handleGenerateAIMessage = async (offeringId) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    if (!aiFanName.trim()) { toast.error("Please enter your name"); return; }
    setReviewPurchase({ item_type: "ai_message", item_id: offeringId, quantity: 1,
      details: { fan_name: aiFanName.trim(), custom_message: aiCustomMsg.trim() } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
          <h1 className="font-['Unbounded'] text-2xl text-white mb-2">Artist Not Found</h1>
          <Link to="/fandom" className="text-[#EC008C]">Browse Artists</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="artist-profile-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/fandom">
            <Button variant="outline" className="border-[#1A2030] text-white hover:bg-[#0A0E18]">
              Browse Artists
            </Button>
          </Link>
        </div>
      </header>

      {/* Test-account ribbon — full-width sticky warning so no fan mistakes a
          demo artist for a live seller. Shown only when admin has flagged. */}
      {artist.is_test_account && (
        <div
          className="fixed top-[72px] left-0 right-0 z-40 bg-[#FFD466] text-black py-2 text-center text-xs font-black uppercase tracking-widest"
          data-testid="test-account-ribbon"
        >
          ⚠ Test Account Only — Not for real purchases. Do not send real money.
        </div>
      )}

      {/* Profile Header */}
      <div className={`${artist.is_test_account ? "pt-[105px]" : "pt-20"} relative`}>
        <div 
          className="h-64 bg-cover bg-center"
          style={{ 
            backgroundImage: artist.cover_image 
              ? `url(${artist.cover_image})` 
              : `url(https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/8nkhg6wh_OnBzn.jpg)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/50 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 -mt-24 relative z-10">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-[#1A2030] border-4 border-[#000000] overflow-hidden flex-shrink-0">
              {artist.profile_image ? (
                <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="w-16 h-16 text-[#8A92A0]" />
                </div>
              )}
            </div>

            <div className="flex-1 pt-4">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="font-['Unbounded'] text-3xl md:text-4xl font-bold text-white" data-testid="artist-name">
                  {artist.stage_name}
                </h1>
                {artist.is_verified && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#EC008C]/10" data-testid="verified-badge">
                    <BadgeCheck className="w-5 h-5 text-[#EC008C]" />
                    <span className="text-xs font-semibold text-[#EC008C]">Verified</span>
                  </div>
                )}
                {artist.store_live && !artist.is_test_account && (
                  <div
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#8DB87A]/15 border border-[#8DB87A]/40"
                    data-testid="live-store-badge"
                    title="Payment-verified — this artist is fully set up to accept payments"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8DB87A] opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8DB87A]"></span>
                    </span>
                    <span className="text-xs font-semibold text-[#8DB87A] uppercase tracking-wider">Live Store</span>
                  </div>
                )}
                {artist.is_test_account && (
                  <div
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFD466]/15 border border-[#FFD466]/50"
                    data-testid="test-account-badge"
                  >
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#FFD466]">
                      Test Account
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4 mb-3 flex-wrap">
                {artist.genre && (
                  <span className="text-[#EC008C] font-medium">{artist.genre}</span>
                )}
                {artist.location && (
                  <span className="text-[#8A92A0] flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {artist.location}
                  </span>
                )}
                <span className="text-[#8A92A0] flex items-center gap-1" data-testid="follower-count">
                  <Users className="w-4 h-4" /> {followerCount.toLocaleString()} followers
                </span>
              </div>
              
              {artist.bio && (
                <p className="text-[#8A92A0] max-w-2xl leading-relaxed mb-4">{artist.bio}</p>
              )}
              
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleFollow}
                  data-testid="follow-btn"
                  className={following 
                    ? "bg-[#1A2030] text-white hover:bg-[#3F3F46] rounded-full px-6" 
                    : "bg-[#FF6BC5] text-[#000000] hover:bg-[#33F3FF] rounded-full px-6"
                  }
                >
                  {following ? (
                    <><UserMinus className="w-4 h-4 mr-2" /> Following</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" /> Follow</>
                  )}
                </Button>
                
                {isAuthenticated && (
                  <Button
                    onClick={() => navigate(`/messages/${artistId}`)}
                    className="bg-[#1A2030] text-white hover:bg-[#3F3F46] rounded-full px-6"
                    data-testid="message-artist-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Message
                  </Button>
                )}

                {isAuthenticated && artist?.artist_id && (
                  <TipButton
                    token={token}
                    artistId={artist.artist_id}
                    artistName={artist.stage_name}
                  />
                )}
                
                <div className="relative group">
                  <Button
                    className="bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white rounded-full w-10 h-10 p-0"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                  <div className="absolute top-full right-0 mt-2 hidden group-hover:flex flex-col gap-1 card p-2 min-w-[180px] z-20" data-testid="share-dropdown">
                    <button
                      onClick={() => { window.open(`https://twitter.com/intent/tweet?text=Check out ${artist.stage_name} on StardomFans.com!&url=${encodeURIComponent(window.location.href)}`, '_blank'); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#8A92A0] hover:text-white hover:bg-[#1A2030] transition-colors"
                      data-testid="share-twitter"
                    >
                      <Twitter className="w-4 h-4" /> Share on X
                    </button>
                    <button
                      onClick={() => {
                        const url = window.location.href;
                        const text = `Check out ${artist.stage_name} on StardomFans.com — ${url}`;
                        if (navigator.share) {
                          navigator.share({ title: artist.stage_name, text, url }).catch(() => {});
                        } else {
                          navigator.clipboard.writeText(text);
                          toast.success("Share text copied — paste it anywhere.");
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#8A92A0] hover:text-white hover:bg-[#1A2030] transition-colors"
                      data-testid="share-more"
                    >
                      <Mail className="w-4 h-4" /> Share
                    </button>
                    <button
                      onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#8A92A0] hover:text-white hover:bg-[#1A2030] transition-colors"
                      data-testid="share-copy"
                    >
                      <LinkIcon className="w-4 h-4" /> Copy Link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#0A0E18] border border-[#1A2030] p-1 mb-8 flex-wrap">
            <TabsTrigger 
              value="content"
              className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
              data-testid="tab-content"
            >
              <Music className="w-4 h-4 mr-2" />
              Music & Videos ({content.length})
            </TabsTrigger>
            <TabsTrigger 
              value="events"
              className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
              data-testid="tab-events"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger 
              value="store"
              className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
              data-testid="tab-store"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Store ({products.length})
            </TabsTrigger>
            {subscriptionTiers.length > 0 && (
              <TabsTrigger 
                value="support"
                className="data-[state=active]:bg-[#D14A85] data-[state=active]:text-white"
                data-testid="tab-support"
              >
                <Heart className="w-4 h-4 mr-2" />
                Support
              </TabsTrigger>
            )}
            {experiences.length > 0 && (
              <TabsTrigger 
                value="experiences"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-experiences"
              >
                <Star className="w-4 h-4 mr-2" />
                Experiences ({experiences.length})
              </TabsTrigger>
            )}
            {aiOfferings.length > 0 && (
              <TabsTrigger 
                value="ai-messages"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-ai-messages"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Messages
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="leaderboard"
              className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
              data-testid="tab-leaderboard"
            >
              <Crown className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
            {collectibles.length > 0 && (
              <TabsTrigger 
                value="collectibles"
                className="data-[state=active]:bg-[#EC008C] data-[state=active]:text-[#000000]"
                data-testid="tab-collectibles"
              >
                <Star className="w-4 h-4 mr-2" />
                Drops ({collectibles.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Content Tab with Player */}
          <TabsContent value="content">
            {content.length === 0 ? (
              <div className="card p-12 text-center">
                <Music className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <p className="text-[#8A92A0]">No content uploaded yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {content.map((item) => {
                  const isPlaying = playingId === item.content_id;
                  const likeInfo = likes[item.content_id] || {};
                  const itemComments = comments[item.content_id] || [];
                  const isCommentsOpen = showComments === item.content_id;
                  
                  return (
                    <div key={item.content_id} className="card overflow-hidden group" data-testid={`content-${item.content_id}`}>
                      {/* Player Area */}
                      <div className="aspect-video bg-[#0A0E18] relative">
                        {(item.content_type === "video" || item.content_type === "livestream") ? (
                          <>
                            <video
                              ref={(el) => { videoRefs.current[item.content_id] = el; }}
                              src={`${API}/content/${item.content_id}/stream`}
                              className="w-full h-full object-contain bg-black"
                              preload="metadata"
                              playsInline
                              data-testid={`video-player-${item.content_id}`}
                            />
                            <div 
                              className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-all ${isPlaying ? 'bg-transparent' : 'bg-black/40'}`}
                              onClick={() => toggleVideo(item.content_id)}
                            >
                              {!isPlaying && (
                                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                                  <Play className="w-6 h-6 text-white ml-1" />
                                </div>
                              )}
                            </div>
                            {isPlaying && (
                              <button className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center" onClick={() => toggleVideo(item.content_id)}>
                                <Pause className="w-4 h-4 text-white" />
                              </button>
                            )}
                          </>
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center relative cursor-pointer"
                            onClick={() => togglePlay(item.content_id)}
                            data-testid={`play-btn-${item.content_id}`}
                          >
                            {item.thumbnail ? (
                              <img src={item.thumbnail} alt={item.title} className="w-full h-full object-contain bg-black" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#121214] to-[#1a1a2e]">
                                <Music className="w-12 h-12 text-[#EC008C]/40" />
                              </div>
                            )}
                            <div className={`absolute inset-0 flex items-center justify-center transition-all ${isPlaying ? 'bg-black/30' : 'bg-black/0 hover:bg-black/40'}`}>
                              <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                                isPlaying ? 'bg-[#EC008C] scale-100' : 'bg-white/20 backdrop-blur scale-0 group-hover:scale-100'
                              }`}>
                                {isPlaying ? <Pause className="w-6 h-6 text-[#000000]" /> : <Play className="w-6 h-6 text-white ml-1" />}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Progress Bar */}
                        {isPlaying && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 h-1.5 bg-[#1A2030] cursor-pointer" 
                            onClick={(e) => seekTo(e, item.content_id, item.content_type === "video" || item.content_type === "livestream")} 
                            data-testid={`seek-bar-${item.content_id}`}
                          >
                            <div className="h-full bg-[#EC008C] transition-all duration-200 rounded-r" style={{ width: `${progress}%` }} />
                          </div>
                        )}
                        <span className={`absolute top-3 right-3 px-2 py-0.5 rounded text-xs font-medium ${
                          item.content_type === "music" ? "bg-[#EC008C]/20 text-[#EC008C]" : "bg-[#FF6BC5]/20 text-[#FF6BC5]"
                        }`}>
                          {item.content_type}
                        </span>
                      </div>
                      
                      {/* Info */}
                      <div className="p-4">
                        <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                        {item.description && <p className="text-sm text-[#6A7280] mb-2 line-clamp-2">{item.description}</p>}
                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="text-[#8A92A0]">{(item.plays || 0).toLocaleString()} plays</span>
                          {isPlaying && (
                            <span className="text-[#EC008C] font-mono text-xs">{formatTime(currentTime)} / {formatTime(duration)}</span>
                          )}
                        </div>
                        
                        {/* Like & Comment Actions */}
                        <div className="flex items-center gap-3 border-t border-[#1A2030] pt-3">
                          <button
                            onClick={() => handleLike(item.content_id)}
                            data-testid={`like-btn-${item.content_id}`}
                            className={`flex items-center gap-1.5 text-sm transition-colors ${
                              likeInfo.user_liked ? 'text-[#D14A85]' : 'text-[#8A92A0] hover:text-[#D14A85]'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${likeInfo.user_liked ? 'fill-[#D14A85]' : ''}`} />
                            {likeInfo.like_count || 0}
                          </button>
                          <button
                            onClick={() => {
                              if (isCommentsOpen) {
                                setShowComments(null);
                              } else {
                                loadComments(item.content_id);
                                setShowComments(item.content_id);
                              }
                            }}
                            data-testid={`comment-toggle-${item.content_id}`}
                            className="flex items-center gap-1.5 text-sm text-[#8A92A0] hover:text-[#FF6BC5] transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Comments
                          </button>
                        </div>
                        
                        {/* Comments Panel */}
                        {isCommentsOpen && (
                          <div className="mt-3 border-t border-[#1A2030] pt-3 space-y-3" data-testid={`comments-panel-${item.content_id}`}>
                            {/* Comment Input */}
                            <div className="flex gap-2">
                              <input
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleComment(item.content_id)}
                                placeholder={isAuthenticated ? "Write a comment..." : "Sign in to comment"}
                                disabled={!isAuthenticated}
                                className="flex-1 bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
                                data-testid={`comment-input-${item.content_id}`}
                              />
                              <button
                                onClick={() => handleComment(item.content_id)}
                                disabled={!commentText.trim() || !isAuthenticated}
                                className="px-3 py-2 rounded-lg bg-[#EC008C] text-[#000000] disabled:opacity-30"
                                data-testid={`comment-submit-${item.content_id}`}
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Comments List */}
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {itemComments.length === 0 ? (
                                <p className="text-xs text-[#6A7280] text-center py-2">No comments yet</p>
                              ) : (
                                itemComments.map((c) => (
                                  <div key={c.comment_id} className="flex gap-2" data-testid={`comment-${c.comment_id}`}>
                                    <div className="w-6 h-6 rounded-full bg-[#1A2030] flex-shrink-0 flex items-center justify-center overflow-hidden">
                                      {c.user_picture ? (
                                        <img src={c.user_picture} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <Users className="w-3 h-3 text-[#8A92A0]" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs font-semibold text-[#8A92A0]">{c.user_name}</span>
                                      <p className="text-sm text-white break-words">{c.text}</p>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {events.length === 0 ? (
              <div className="card p-12 text-center">
                <Calendar className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <p className="text-[#8A92A0]">No upcoming events.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <Link
                    key={event.event_id}
                    to={`/events/${event.event_id}`}
                    className="card p-6 group"
                    data-testid={`event-${event.event_id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-['Unbounded'] text-xl font-semibold text-white group-hover:text-[#EC008C] transition-colors">
                          {event.title}
                        </h3>
                        <p className="text-[#8A92A0] flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" /> {event.venue} &bull; {event.location}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        event.status === "upcoming" ? "bg-[#EC008C]/10 text-[#EC008C]" : "bg-[#A1A1AA]/10 text-[#8A92A0]"
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-xs text-[#8A92A0]">Date</p>
                        <p className="text-white font-medium">{event.event_date}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#8A92A0]">Price</p>
                        <p className="text-[#EC008C] font-semibold">${event.ticket_price}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#8A92A0]">Available</p>
                        <p className="text-white">{event.tickets_available - event.tickets_sold} tickets</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Store Tab */}
          <TabsContent value="store">
            <CollectibleStoreSection items={collectibles} onBuy={(item) => {
              if (!isAuthenticated) { navigate("/auth"); return; }
              setReviewPurchase({ item_type: "collectible", item_id: item.collectible_id, quantity: 1 });
            }} />
            {/* Trust badge — buyers know the money path bypasses the platform */}
            <div
              className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#0A0E18] border border-[#EC008C]/25 rounded-lg p-4"
              data-testid="checkout-trust-badge"
            >
              <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 border border-[#EC008C]/40 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#EC008C]" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-white font-semibold">
                  Secure checkout by StardomFans Ltd.
                </p>
                <p className="text-xs text-[#8A92A0] mt-0.5">
                  Payments handled by a PCI-DSS Level 1 processor. StardomFans Ltd. never sees your card number.
                </p>
              </div>
            </div>

            {products.length === 0 ? (
              <div className="card p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <p className="text-[#8A92A0]">No products available.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => {
                  const owned = !!ownedMap[product.product_id];
                  const isDigital = ["music", "video", "digital", "album"].includes((product.category || "").toLowerCase());
                  return (
                    <div
                      key={product.product_id}
                      className="card p-4 group"
                      data-testid={`product-${product.product_id}`}
                    >
                      <Link to={`/products/${product.product_id}`} className="block">
                        <div className="aspect-square rounded-lg bg-[#1A2030] mb-3 flex items-center justify-center overflow-hidden">
                          <img src={product.images && product.images.length > 0 ? product.images[0] : "https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/am5ifx80_Korean%20idols%20in%20casual%20fashion.%20%282%29.png"} alt={product.name} className="w-full h-full object-contain bg-black" />
                        </div>
                        <h3 className="font-semibold text-white mb-1 group-hover:text-[#EC008C] transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-sm text-[#8A92A0] mb-2">{product.category}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[#EC008C] font-semibold">${product.price}</span>
                          {owned ? (
                            <span className="text-xs text-[#8DB87A] font-semibold">OWNED</span>
                          ) : (
                            <span className="text-xs text-[#8A92A0]">{product.stock} left</span>
                          )}
                        </div>
                      </Link>
                      {(product.preview_url || (isDigital && owned)) && (
                        <button
                          onClick={(e) => { e.preventDefault(); openTeaser(product); }}
                          className={`mt-3 w-full h-9 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${owned ? "bg-[#EC008C] text-black hover:bg-[#FF3399]" : "border border-[#EC008C]/40 text-[#EC008C] hover:bg-[#EC008C]/10"}`}
                          data-testid={`storefront-teaser-btn-${product.product_id}`}
                        >
                          {owned ? (
                            <><Download className="w-3.5 h-3.5" /> Download Full File</>
                          ) : (
                            <><Play className="w-3.5 h-3.5" /> Play Teaser</>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Support/Subscribe Tab */}
          <TabsContent value="support">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                  Support {artist.stage_name}
                </h2>
                <p className="text-[#8A92A0]">
                  Subscribe monthly to get exclusive perks and support this artist directly.
                </p>
              </div>
              {/* Currency Selector */}
              <div className="flex items-center justify-center gap-2 mb-8 flex-wrap" data-testid="currency-selector">
                <Globe className="w-4 h-4 text-[#8A92A0]" />
                {CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setSubCurrency(c)}
                    data-testid={`currency-${c.code}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      subCurrency.code === c.code
                        ? "bg-[#EC008C]/10 border-[#EC008C] text-[#EC008C]"
                        : "bg-[#0A0E18] border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40"
                    }`}
                  >
                    {c.symbol} {c.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {subscriptionTiers.map((tier) => (
                  <div 
                    key={tier.tier_id} 
                    className="card p-6 relative overflow-hidden"
                    data-testid={`subscription-tier-${tier.tier_id}`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D14A85] to-[#EC008C]" />
                    {tier.tier_level === "vip" && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#D14A85]/20 border border-[#D14A85]/40">
                        <span className="text-[#D14A85] text-xs font-bold flex items-center gap-1"><Crown className="w-3 h-3" /> VIP</span>
                      </div>
                    )}
                    {tier.tier_level === "free" && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#A1A1AA]/20 border border-[#A1A1AA]/40">
                        <span className="text-[#8A92A0] text-xs font-bold">FREE</span>
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="font-['Unbounded'] text-xl font-semibold text-white mb-2">
                        {tier.name}
                      </h3>
                      <p className="text-3xl font-bold text-[#EC008C]">
                        {subCurrency.symbol}{tier.price}<span className="text-sm text-[#8A92A0] font-normal">/month</span>
                      </p>
                    </div>
                    {tier.description && (
                      <p className="text-[#8A92A0] text-sm mb-4">{tier.description}</p>
                    )}
                    {tier.benefits && tier.benefits.length > 0 && (
                      <ul className="space-y-3 mb-6">
                        {tier.benefits.map((benefit, idx) => (
                          <li key={idx} className="text-sm text-white flex items-start gap-2">
                            <Check className="w-4 h-4 text-[#EC008C] mt-0.5 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      onClick={() => handleSubscribe(tier.tier_id)}
                      disabled={subscribing === tier.tier_id}
                      className="w-full bg-[#D14A85] text-white hover:bg-[#FF3377] neon-accent"
                      data-testid={`subscribe-btn-${tier.tier_id}`}
                    >
                      {subscribing === tier.tier_id ? "Processing..." : (
                        <>
                          <Heart className="w-4 h-4 mr-2" />
                          Subscribe
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Experiences Tab */}
          <TabsContent value="experiences">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                  Exclusive Experiences
                </h2>
                <p className="text-[#8A92A0]">
                  Book a personal experience with {artist.stage_name}. Limited spots available.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {experiences.map((exp) => {
                  const slotsLeft = exp.slots_available - exp.slots_booked;
                  const typeLabels = { fan_meet: "Supporter Meet", video_call: "1-on-1 Video Call", backstage: "Backstage Access" };
                  const TypeIcon = exp.experience_type === "video_call" ? Phone : exp.experience_type === "backstage" ? Users : Star;
                  return (
                    <div key={exp.experience_id} className="card p-6 relative overflow-hidden" data-testid={`exp-card-${exp.experience_id}`}>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D14A85] to-[#EC008C]" />
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#D14A85]/10 flex items-center justify-center">
                          <TypeIcon className="w-6 h-6 text-[#D14A85]" />
                        </div>
                        <div>
                          <span className="text-xs font-medium text-[#EC008C] uppercase tracking-wider">{typeLabels[exp.experience_type] || exp.experience_type}</span>
                          <h3 className="font-['Unbounded'] text-lg font-semibold text-white">{exp.title}</h3>
                        </div>
                      </div>
                      {exp.description && <p className="text-[#8A92A0] text-sm mb-4 leading-relaxed">{exp.description}</p>}
                      <div className="flex items-center gap-4 mb-4 text-sm text-[#8A92A0]">
                        <span>{exp.duration_minutes} min</span>
                        <span className={slotsLeft > 0 ? 'text-[#8DB87A]' : 'text-[#D14A85]'}>
                          {slotsLeft > 0 ? `${slotsLeft} spot${slotsLeft > 1 ? 's' : ''} left` : 'Sold Out'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-2xl font-bold text-[#EC008C]">
                          {subCurrency.symbol}{exp.price}
                        </p>
                        <Button
                          onClick={() => handleBookExperience(exp.experience_id)}
                          disabled={slotsLeft <= 0 || bookingExpId === exp.experience_id}
                          className="bg-[#D14A85] text-white hover:bg-[#FF3377] neon-accent px-6"
                          data-testid={`book-exp-${exp.experience_id}`}
                        >
                          {bookingExpId === exp.experience_id ? "Processing..." : slotsLeft <= 0 ? "Sold Out" : "Book Now"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* AI Messages Tab */}
          <TabsContent value="ai-messages">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                  AI Personalized Messages
                </h2>
                <p className="text-[#8A92A0]">
                  Get a personal message from {artist.stage_name} with a custom AI-generated scene featuring your name!
                </p>
              </div>

              {/* AI Result Display */}
              {aiResult && (
                <div className="card p-8 mb-8 relative overflow-hidden" data-testid="ai-result-card">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D14A85] via-[#EC008C] to-[#FF6BC5]" />
                  <div className="text-center mb-4">
                    <span className="text-xs text-[#EC008C] uppercase tracking-widest">Your Personal Message</span>
                  </div>
                  {aiResult.scene_image_url && (
                    <div className="rounded-xl overflow-hidden mb-6 max-w-md mx-auto">
                      <img src={aiResult.scene_image_url} alt="Custom AI Scene" className="w-full" />
                    </div>
                  )}
                  <div className="max-w-xl mx-auto bg-[#0A0E18] rounded-xl p-6 border border-[#1A2030]">
                    <p className="text-white text-lg leading-relaxed italic">"{aiResult.personalized_text}"</p>
                    <p className="text-right text-[#EC008C] font-medium mt-4">— {aiResult.artist_name}</p>
                  </div>
                  <div className="text-center mt-4">
                    <button onClick={() => setAiResult(null)} className="text-sm text-[#8A92A0] hover:text-white">Close</button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {aiOfferings.map((off) => {
                  const styleColors = { fantasy: "#C084FC", concert: "#F87171", space: "#60A5FA", nature: "#32D583", cyberpunk: "#C084FC" };
                  const color = styleColors[off.scene_style] || "#32D583";
                  return (
                    <div key={off.offering_id} className="card p-6 relative overflow-hidden" data-testid={`ai-offering-card-${off.offering_id}`}>
                      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(to right, ${color}, #32D583)` }} />
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
                          <Sparkles className="w-6 h-6" style={{ color }} />
                        </div>
                        <div>
                          <span className="text-xs font-medium uppercase tracking-wider" style={{ color }}>{off.scene_style} Scene</span>
                          <h3 className="font-['Unbounded'] text-lg font-semibold text-white">{off.title}</h3>
                        </div>
                      </div>
                      {off.description && <p className="text-[#8A92A0] text-sm mb-4">{off.description}</p>}
                      <p className="text-2xl font-bold text-[#EC008C] mb-4">{subCurrency.symbol}{off.price}</p>
                      
                      {/* Generation Form */}
                      <div className="space-y-3 border-t border-[#1A2030] pt-4">
                        <input
                          type="text"
                          value={aiFanName}
                          onChange={(e) => setAiFanName(e.target.value)}
                          placeholder="Your name (for the scene)"
                          className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#71717A]"
                          data-testid={`ai-fan-name-${off.offering_id}`}
                        />
                        <input
                          type="text"
                          value={aiCustomMsg}
                          onChange={(e) => setAiCustomMsg(e.target.value)}
                          placeholder="Custom message (optional)"
                          className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#71717A]"
                          data-testid={`ai-custom-msg-${off.offering_id}`}
                        />
                        <Button
                          onClick={() => handleGenerateAIMessage(off.offering_id)}
                          disabled={aiGenerating === off.offering_id}
                          className="w-full bg-[#D14A85] text-white hover:bg-[#FF3377] neon-accent"
                          data-testid={`ai-generate-btn-${off.offering_id}`}
                        >
                          {aiGenerating === off.offering_id ? (
                            <span className="flex items-center gap-2">Generating... (may take 15-30s)</span>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" /> Get My AI Message</>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                  Fan Leaderboard
                </h2>
                <p className="text-[#8A92A0]">Top supporters of {artist.stage_name}</p>
              </div>
              {leaderboard.length === 0 ? (
                <div className="card p-12 text-center">
                  <Crown className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                  <h3 className="text-white font-semibold text-lg mb-2">No fans ranked yet</h3>
                  <p className="text-[#8A92A0] text-sm">Be the first to support this artist and claim the top spot!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leaderboard.map((fan) => {
                    const badgeColors = {
                      "Top Fan": "bg-[#EC008C]/10 text-[#EC008C] border-[#EC008C]/30",
                      "Rising Star": "bg-[#D14A85]/10 text-[#D14A85] border-[#D14A85]/30",
                      "VIP Supporter": "bg-[#FF6BC5]/10 text-[#FF6BC5] border-[#FF6BC5]/30",
                      "Loyal Fan": "bg-[#A1A1AA]/10 text-[#8A92A0] border-[#A1A1AA]/30"
                    };
                    return (
                      <div key={fan.user_id} className={`card p-4 flex items-center gap-4 ${fan.rank <= 3 ? 'border-[#EC008C]/20' : ''}`} data-testid={`leaderboard-rank-${fan.rank}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                          fan.rank === 1 ? 'bg-[#EC008C] text-[#000000]' :
                          fan.rank === 2 ? 'bg-[#A1A1AA] text-[#000000]' :
                          fan.rank === 3 ? 'bg-[#CD7F32] text-[#000000]' :
                          'bg-[#1A2030] text-[#8A92A0]'
                        }`}>
                          {fan.rank}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {fan.picture ? <img src={fan.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#8A92A0]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{fan.name}</p>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeColors[fan.badge] || badgeColors["Loyal Fan"]}`}>
                              {fan.badge}
                            </span>
                            <span className="text-xs text-[#4A5260]">{fan.purchase_count} purchases</span>
                          </div>
                        </div>
                        <span className="text-[#EC008C] font-bold">${fan.total_spent.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Community Link */}
              <div className="card p-6 mt-8 text-center">
                <MessageCircle className="w-8 h-8 text-[#D14A85] mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-1">Join the Community</h3>
                <p className="text-[#8A92A0] text-sm mb-4">Chat with other fans of {artist.stage_name}</p>
                <Link to={`/community/${artist.artist_id}`}>
                  <Button className="bg-[#D14A85] text-white hover:bg-[#FF3377] rounded-full px-6" data-testid="community-link-btn">
                    <MessageCircle className="w-4 h-4 mr-2" /> Join Community
                  </Button>
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* Collectibles/Drops Tab */}
          <TabsContent value="collectibles">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Limited Drops</h2>
                <p className="text-[#8A92A0]">Exclusive digital collectibles from {artist.stage_name}. Once they're gone, they're gone.</p>
              </div>
              <CollectibleStoreSection items={collectibles} prefix="drop-collectible" onBuy={(item) => {
                if (!isAuthenticated) { navigate("/auth"); return; }
                setReviewPurchase({ item_type: "collectible", item_id: item.collectible_id, quantity: 1 });
              }} />
            </div>
          </TabsContent>
        </Tabs>

        {/* Pay-Per-Interaction Section */}
        <div className="max-w-2xl mx-auto mt-12 card p-6" data-testid="paid-interaction-section">
          <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2 text-center">Send a Paid Message</h3>
          <p className="text-[#8A92A0] text-sm text-center mb-6">Get noticed! Highlighted messages ($5) or paid replies ($2) go directly to {artist.stage_name}.</p>
          <div className="space-y-3">
            <textarea
              id="paid-msg-text"
              placeholder={isAuthenticated ? `Write your message to ${artist.stage_name}...` : "Sign in to send a paid message"}
              disabled={!isAuthenticated}
              maxLength={500}
              className="w-full bg-[#050810] border border-[#1A2030] rounded-xl px-4 py-3 text-sm text-white placeholder-[#52525B] resize-none min-h-[80px] focus:outline-none focus:border-[#EC008C]/50"
              data-testid="paid-msg-input"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" id="highlight-check" className="rounded border-[#1A2030] bg-[#050810]" data-testid="highlight-checkbox" />
                  <span className="text-sm text-[#8A92A0]">Highlight ($5)</span>
                </label>
                <span className="text-xs text-[#4A5260]">or $2 regular</span>
              </div>
              <Button
                onClick={async () => {
                  if (!isAuthenticated) { navigate("/auth"); return; }
                  const text = document.getElementById("paid-msg-text")?.value;
                  const highlight = document.getElementById("highlight-check")?.checked;
                  if (!text?.trim()) { toast.error("Please write a message"); return; }
                  setReviewPurchase({ item_type: "interaction", item_id: artistId, quantity: 1,
                    details: { text: text.trim(), highlight: !!highlight } });
                }}
                className="bg-[#D14A85] text-white hover:bg-[#FF3377] px-6"
                data-testid="send-paid-msg-btn"
              >
                Send Paid Message
              </Button>
            </div>
          </div>
        </div>
      </main>

      <PurchaseReview purchase={reviewPurchase} onClose={() => setReviewPurchase(null)} />
      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>

      <TeaserModal
        open={teaser.open}
        onClose={() => setTeaser({ open: false, item: null, purchaseState: null })}
        item={teaser.item}
        purchaseState={teaser.purchaseState}
        token={token}
        onBuyClick={() => {
          if (teaser.item?.productId) {
            setTeaser({ open: false, item: null, purchaseState: null });
            navigate(`/products/${teaser.item.productId}`);
          }
        }}
        onDownloaded={() => {
          // Refresh purchase counters after download
          if (!token) return;
          axios.get(`${API}/orders/my`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
              const map = {};
              (r.data?.orders || []).forEach((o) => {
                if (o.product_id) {
                  map[o.product_id] = {
                    owned: true,
                    downloads_used: o.downloads_used || 0,
                    downloads_remaining: Number.isFinite(o.downloads_remaining) ? o.downloads_remaining : 3,
                    max_downloads: o.max_downloads || 3,
                  };
                }
              });
              setOwnedMap(map);
            })
            .catch(() => {});
        }}
      />
    </div>
  );
};

export default ArtistProfile;
